import { Paginator } from 'array-paginator';
import { defineStore } from 'pinia';
import { ResolveListForExportCallback } from 'src/stores/_infrastructure/ResolveListForExportCallback';
import { completeListExportViewFor } from 'src/stores/_infrastructure/completeListExportView';
import { resolveDesktopModuleDateFilterForQuery } from 'src/stores/_infrastructure/dateTimeRangeQueryValue';
import { dedupByAggregateId } from 'src/stores/_infrastructure/dedupByAggregateId';
import {
	COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
	prepareQueryForCompleteListExport,
} from 'src/stores/_infrastructure/prepareQueryForCompleteListExport';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { GetAllUsersWithAccessToTheModuleForTheUserFilterQuery } from 'src/stores/genericActivityList/queries';
import {
	NOTE_LIST_GENERIC_ACTIVITY_LIST_SORT,
	NOTE_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
} from 'src/stores/noteList/localStorageKeys';
import {
	GetTheNoteModuleBoxesForTheUserQuery,
	GetTheNoteModuleNotesForTheUserQuery,
} from 'src/stores/noteList/queries';
import createNoteListState from 'src/stores/noteList/state';
import { TSMap } from 'typescript-map';

import { DateRangeSelection } from 'src/models/common';
import { FilterBoxes, FilterBoxesRunType, FilterUsers, FilterUsersRunType } from 'src/models/filter';
import {
	ActivityListTypeEnum,
	GenericActivityListPagination,
	GenericActivityListSort,
} from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { ModuleNameEnum } from 'src/models/module/enums';
import { NoteListItem, NoteListItemRunType } from 'src/models/noteList';
import { SerializedQuery } from 'src/models/query';
import { StatusEnum } from 'src/models/status/enums';

import {
	removeTheAggregateIdsFromTheCollection,
	setTheStateForTheAggregateIds,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { createCompareFunction } from 'src/util/collection/customCompareFunction';
import { DEFAULT_LIST_INSTANCE_ID, deriveListStoreId, isDefaultInstance } from 'src/util/listInstance/listInstanceKeys';

import {
	GetAllUsersWithAccessToTheModuleForTheUserFilterNoteListCallBack,
	GetTheAllNoteModuleNotesForTheUserCallBack,
	GetTheMineNoteModuleNotesForTheUserCallBack,
	GetTheNoteModuleBoxesForTheUserQueryCallBack,
	GetTheOpenNoteModuleNotesForTheUserCallBack,
} from './callbacks';
import {
	CompleteListExportFetchPayload,
	DeleteSelectedAggregateIdsPayload,
	ListFetchPayload,
	QueryStorePayload,
	SetDateFilterPayload,
	SetDateTimeRangeFilterPayload,
	SetFilteredBoxesPayload,
	SetFilteredUsersPayload,
	SetListPayload,
	SetListStatePayload,
	SetPaginationPagePayload,
	SetPaginationPayload,
	SetSelectedSharedQueryPayload,
	SetSortPayload,
} from './interfaces';

const DEFAULT_ROWS_PER_PAGE = 100;
const DEFAULT_LIST_SORT: GenericActivityListSort = {
	direction: 'DESC',
	property: 'timestamp',
};

const createDefaultPaginationEntry = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: DEFAULT_ROWS_PER_PAGE,
});

const createDefaultPaginationMap = (): TSMap<ActivityListTypeEnum, GenericActivityListPagination> =>
	new TSMap<ActivityListTypeEnum, GenericActivityListPagination>([
		[ActivityListTypeEnum.ALL, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.MINE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.OPEN, createDefaultPaginationEntry()],
	]);

const replaceActiveQueryStoreSubscription = <T>({
	matchesLogicalView,
	nextSerializedQuery,
	parseQuery,
	queryStore,
	subscriptions,
}: {
	matchesLogicalView: (serializedQuery: SerializedQuery) => boolean;
	nextSerializedQuery: SerializedQuery;
	parseQuery: (serializedQuery: SerializedQuery) => T;
	queryStore: ListFetchPayload['queryStore'];
	subscriptions: SerializedQuery[];
}): SerializedQuery[] => {
	const staleSubscriptions = subscriptions.filter(matchesLogicalView);

	staleSubscriptions.forEach((serializedQuery) => {
		void queryStore?.unsubscribeQuery(parseQuery(serializedQuery));
	});

	return [...subscriptions.filter((serializedQuery) => !matchesLogicalView(serializedQuery)), nextSerializedQuery];
};

// Task 3.1f: isolierte gespeicherte-Listen-Tabs (`$id !== 'noteList'`) sollen view-scoped Zähler/Zeilen zeigen —
// deshalb unterdrücken ihre Query-Konstruktionen die Box→Custom-Kopplung (sonst kollabieren Offen/Meine/Alle auf
// dieselbe Custom-Query → 8/8/8). Der Default-Store behält die klassische Kopplung (byte-identisch). Wird an ALLE
// Konstruktionsstellen (Dispatch + Register + Export) gereicht, damit Subscription-Identifier konsistent bleiben.
const suppressBoxCustomCouplingForInstance = (storeId: string): boolean => storeId !== 'noteList';

const defineNoteListStore = (id: string) =>
	defineStore(id, {
		actions: {
			addQueryStoreSubscription(serializedQuery: SerializedQuery): void {
				this.queryStoreSubscriptions.push(serializedQuery);
			},
			clearQueryStoreSubscriptions(): void {
				this.queryStoreSubscriptions = [];
			},
			clearSubscriptions(payload: QueryStorePayload): void {
				if (payload.queryStore !== undefined) {
					this.queryStoreSubscriptions.forEach((serializedQuery: SerializedQuery) => {
						switch (serializedQuery.type) {
							case 'GetTheNoteModuleNotesForTheUserQuery':
								void payload.queryStore?.unsubscribeQuery(
									GetTheNoteModuleNotesForTheUserQuery.fromJSON<GetTheNoteModuleNotesForTheUserQuery>(
										serializedQuery.query
									)
								);
								break;
							default:
								break;
						}
					});
				}

				this.clearQueryStoreSubscriptions();
			},
			deleteSelectedAggregateIds(payload: DeleteSelectedAggregateIdsPayload): void {
				const repaginateAndSet = (
					noteList: NoteListItem[],
					totalCount: number,
					applyPage: (pagePayload: SetListPayload) => void
				): void => {
					const noteListCopy = removeTheAggregateIdsFromTheCollection(
						noteList as unknown as never[],
						payload.aggregateIds
					) as unknown as NoteListItem[];

					if (noteListCopy !== noteList) {
						const noteListCopyPaginator = new Paginator(noteListCopy, DEFAULT_ROWS_PER_PAGE, 1);

						while (noteListCopyPaginator.current <= noteListCopyPaginator.total) {
							applyPage({
								page: noteListCopyPaginator.current,
								payload: noteListCopyPaginator.page(noteListCopyPaginator.current) as NoteListItem[],
								totalCount: totalCount,
							});

							noteListCopyPaginator.next();
						}
					}
				};

				repaginateAndSet(this.noteListOpen, this.noteListOpenTotalCount, (listPayload) =>
					this.setOpenNoteList(listPayload)
				);
				repaginateAndSet(this.noteListAll, this.noteListAllTotalCount, (listPayload) =>
					this.setAllNoteList(listPayload)
				);
				repaginateAndSet(this.noteListMine, this.noteListMineTotalCount, (listPayload) =>
					this.setMineNoteList(listPayload)
				);
			},
			async fetchAllNoteList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setAllNoteList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchAllNoteList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.ALL);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const page = pagination.page;
				const query = new GetTheNoteModuleNotesForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.ALL, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.ALL, queryIdentifier);

				const callback = new GetTheAllNoteModuleNotesForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<NoteListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: NoteListItemRunType,
				});
			},
			async fetchFilterBoxes(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterBoxes([]);
					return;
				}

				await payload.queryStore.query<Array<FilterBoxes>>({
					callback: new GetTheNoteModuleBoxesForTheUserQueryCallBack(this),
					createTime: new Date(),
					query: new GetTheNoteModuleBoxesForTheUserQuery(),
					responseType: 'json',
					runType: FilterBoxesRunType,
				});
			},
			async fetchFilterUsers(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterUsers([]);
					return;
				}

				await payload.queryStore.query<Array<FilterUsers>>({
					callback: new GetAllUsersWithAccessToTheModuleForTheUserFilterNoteListCallBack(this),
					createTime: new Date(),
					query: new GetAllUsersWithAccessToTheModuleForTheUserFilterQuery(ModuleNameEnum.MODULE_NOTE),
					responseType: 'json',
					runType: FilterUsersRunType,
				});
			},
			async fetchMineNoteList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setMineNoteList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchMineNoteList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.MINE);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const page = pagination.page;
				const query = new GetTheNoteModuleNotesForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.MINE, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.MINE, queryIdentifier);

				const callback = new GetTheMineNoteModuleNotesForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<NoteListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: NoteListItemRunType,
				});
			},
			async fetchOpenNoteList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setOpenNoteList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchOpenNoteList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.OPEN);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const page = pagination.page;
				const query = new GetTheNoteModuleNotesForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.OPEN, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.OPEN, queryIdentifier);

				const callback = new GetTheOpenNoteModuleNotesForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<NoteListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: NoteListItemRunType,
				});
			},
			async fetchTheCompleteListForExport(
				payload: CompleteListExportFetchPayload
			): Promise<NoteListItem[] | null> {
				if (payload.queryStore === undefined) {
					return null;
				}

				const totalCountByListType: Partial<Record<ActivityListTypeEnum, number>> = {
					[ActivityListTypeEnum.ALL]: this.noteListAllTotalCount,
					[ActivityListTypeEnum.MINE]: this.noteListMineTotalCount,
					[ActivityListTypeEnum.OPEN]: this.noteListOpenTotalCount,
				};
				const pagination: GenericActivityListPagination = {
					page: 1,
					rowsPerPage:
						(totalCountByListType[payload.activityListType] ?? 0) + COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
				};
				const query = prepareQueryForCompleteListExport(
					new GetTheNoteModuleNotesForTheUserQuery(
						completeListExportViewFor(payload.activityListType),
						resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
						this.filteredBoxIds,
						this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
						payload.searchTerm,
						payload.hiddenSearchTerm,
						pagination,
						this.sortFor(payload.activityListType),
						suppressBoxCustomCouplingForInstance(this.$id)
					)
				);

				let completeList: NoteListItem[] | null = null;

				await payload.queryStore.query<NoteListItem[]>({
					callback: new ResolveListForExportCallback<NoteListItem>((list) => {
						completeList = list;
					}),
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: NoteListItemRunType,
				});

				return completeList;
			},
			isLatestQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): boolean {
				return (this.activeQueryIdentifierByListType.get(activityListType) ?? '') === queryIdentifier;
			},
			registerUnsubscribeFetchAllNoteList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const query = new GetTheNoteModuleNotesForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					this.sortFor(ActivityListTypeEnum.ALL),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceNoteListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchMineNoteList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const query = new GetTheNoteModuleNotesForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					this.sortFor(ActivityListTypeEnum.MINE),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceNoteListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchOpenNoteList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const query = new GetTheNoteModuleNotesForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					this.sortFor(ActivityListTypeEnum.OPEN),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceNoteListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			replaceNoteListQueryStoreSubscription(payload: {
				query: GetTheNoteModuleNotesForTheUserQuery;
				queryStore: ListFetchPayload['queryStore'];
			}): void {
				const nextSerializedQuery: SerializedQuery = {
					query: JSON.stringify(payload.query),
					type: 'GetTheNoteModuleNotesForTheUserQuery',
				};
				const expectedStandardFilterSelection = payload.query.standardFilterSelection;

				this.queryStoreSubscriptions = replaceActiveQueryStoreSubscription({
					matchesLogicalView: (serializedQuery) => {
						if (serializedQuery.type !== nextSerializedQuery.type) {
							return false;
						}

						return (
							GetTheNoteModuleNotesForTheUserQuery.fromJSON<GetTheNoteModuleNotesForTheUserQuery>(
								serializedQuery.query
							).standardFilterSelection === expectedStandardFilterSelection
						);
					},
					nextSerializedQuery,
					parseQuery: (serializedQuery) =>
						GetTheNoteModuleNotesForTheUserQuery.fromJSON<GetTheNoteModuleNotesForTheUserQuery>(
							serializedQuery.query
						),
					queryStore: payload.queryStore,
					subscriptions: this.queryStoreSubscriptions,
				});
			},
			reset(payload: QueryStorePayload): void {
				this.clearSubscriptions({
					queryStore: payload.queryStore,
				});
				this.resetState();
			},
			resetState(): void {
				const today = new Date().toISOString().split('T')[0];
				this.activeQueryIdentifierByListType = new TSMap<ActivityListTypeEnum, string>([
					[ActivityListTypeEnum.ALL, ''],
					[ActivityListTypeEnum.MINE, ''],
					[ActivityListTypeEnum.OPEN, ''],
				]);
				this.dateFilter = today;
				this.today = today;
				this.dateTimeRangeFilter = PreconfiguredTimeRanges.TODAY;
				this.filteredUsers = [];
				this.filteredBoxIds = [];
				this.selectedSharedQueryId = '';
				this.queryStoreSubscriptions = [];
				this.noteListOpenTotalCount = 0;
				this.noteListOpen = [];
				this.noteListOpenSliced.clear();
				this.noteListAllTotalCount = 0;
				this.noteListAll = [];
				this.noteListAllSliced.clear();
				this.noteListMineTotalCount = 0;
				this.noteListMine = [];
				this.noteListMineSliced.clear();
				this.pagination = createDefaultPaginationMap();
			},
			setActiveQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): void {
				this.activeQueryIdentifierByListType.set(activityListType, queryIdentifier);
			},
			setAllNoteList(payload: SetListPayload): void {
				this.noteListAllTotalCount = payload.totalCount;
				this.noteListAllSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				this.noteListAll = dedupByAggregateId(
					[...this.noteListAllSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<NoteListItem>(sort.property as keyof NoteListItem, sort.direction));
			},
			setDateFilterAction(payload: SetDateFilterPayload): void {
				this.setDateFilterValue(payload.dateFilter);
			},
			setDateFilterValue(payload: DateRangeSelection | string): void {
				this.dateFilter = payload || '';
			},
			setDateTimeRangeFilter(payload: SetDateTimeRangeFilterPayload): void {
				this.setDateTimeRangeFilterValue(payload.dateTimeRangeFilter);
			},
			setDateTimeRangeFilterValue(payload: PreconfiguredTimeRanges): void {
				this.dateTimeRangeFilter = payload;
			},
			setFilterBoxes(payload: Array<FilterBoxes>): void {
				this.filterBoxes = payload || [];
			},
			setFilterUsers(payload: Array<FilterUsers>): void {
				this.filterUsers = payload || [];
			},
			setFilteredBoxes(payload: Array<string>): void {
				this.filteredBoxIds = payload || [];
			},
			setFilteredBoxesAction(payload: SetFilteredBoxesPayload): void {
				this.setFilteredBoxes(payload.boxIdFilter);
			},
			setFilteredUsers(payload: Array<FilterUsers>): void {
				this.filteredUsers = payload || [];
			},
			setFilteredUsersAction(payload: SetFilteredUsersPayload): void {
				this.setFilteredUsers(payload.filterUsers);
			},
			setListLoadError(): void {
				const genericActivityListStore = useGenericActivityListStore();
				genericActivityListStore.setListLoadError();
			},
			setListViewModeLarge(payload: boolean): void {
				if (this.$id === 'noteList') {
					localStorage.setItem(NOTE_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE, JSON.stringify(payload));
				}
				this.listViewModeLarge = payload;
			},
			setMineNoteList(payload: SetListPayload): void {
				this.noteListMineTotalCount = payload.totalCount;
				this.noteListMineSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				this.noteListMine = dedupByAggregateId(
					[...this.noteListMineSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<NoteListItem>(sort.property as keyof NoteListItem, sort.direction));
			},
			setOpenNoteList(payload: SetListPayload): void {
				this.noteListOpenTotalCount = payload.totalCount;
				this.noteListOpenSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				this.noteListOpen = dedupByAggregateId(
					[...this.noteListOpenSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<NoteListItem>(sort.property as keyof NoteListItem, sort.direction));
			},
			setPagination(payload: SetPaginationPayload): void {
				this.pagination.set(payload.activityListType, payload.pagination);
			},
			setPaginationAction(payload: SetPaginationPayload): void {
				this.setPagination(payload);
			},
			setPaginationPage(payload: SetPaginationPagePayload): void {
				const pagination = this.paginationFor(payload.activityListType);
				pagination.page = payload.page;
				this.pagination.set(payload.activityListType, pagination);
			},
			setPaginationPageAction(payload: SetPaginationPagePayload): void {
				this.setPaginationPage(payload);
			},
			setSelectedSharedQuery(payload: string): void {
				this.selectedSharedQueryId = payload || '';
			},
			setSelectedSharedQueryAction(payload: SetSelectedSharedQueryPayload): void {
				this.setSelectedSharedQuery(payload.selectedSharedQuery ?? '');
			},
			setSort(payload: SetSortPayload): void {
				this.sort.set(payload.activityListType, payload.sort);
			},
			setSortAction(payload: SetSortPayload): void {
				this.setSort(payload);
				if (this.$id === 'noteList') {
					localStorage.setItem(NOTE_LIST_GENERIC_ACTIVITY_LIST_SORT, JSON.stringify(this.sort.toJSON()));
				}
			},
			setTheNoteStateToReady(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.READY);
			},
			setTheNoteStateToSeen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.SEEN);
			},
			setTheNoteStateToUnseen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.UNSEEN);
			},
			setTheStateForAggregateIds(aggregateIds: string[], status: StatusEnum): void {
				let openCountDelta = 0;
				aggregateIds.forEach((aggregateId) => {
					const known =
						this.noteListOpen.find((item) => item.aggregateId === aggregateId) ??
						this.noteListAll.find((item) => item.aggregateId === aggregateId) ??
						this.noteListMine.find((item) => item.aggregateId === aggregateId);
					if (!known) {
						return;
					}
					const wasReady = known.statusButton.status === StatusEnum.READY;
					const willBeReady = status === StatusEnum.READY;
					if (!wasReady && willBeReady) {
						openCountDelta -= 1;
					} else if (wasReady && !willBeReady) {
						openCountDelta += 1;
					}
				});
				if (openCountDelta !== 0) {
					this.noteListOpenTotalCount = Math.max(0, this.noteListOpenTotalCount + openCountDelta);
				}

				const setPagedList = <T>(
					entries: T[],
					totalCount: number,
					applyPage: (payload: { page: number; payload: T[]; totalCount: number }) => void
				): void => {
					if (entries.length === 0) {
						return;
					}

					const paginator = new Paginator(entries, DEFAULT_ROWS_PER_PAGE, 1);
					while (paginator.current <= paginator.total) {
						applyPage({
							page: paginator.current,
							payload: paginator.page(paginator.current) as T[],
							totalCount: totalCount,
						});
						paginator.next();
					}
				};

				setPagedList<NoteListItem>(
					setTheStateForTheAggregateIds(
						this.noteListOpen as never[],
						aggregateIds,
						status
					) as unknown as NoteListItem[],
					this.noteListOpenTotalCount,
					(payload) => this.setOpenNoteList(payload)
				);
				setPagedList<NoteListItem>(
					setTheStateForTheAggregateIds(
						this.noteListAll as never[],
						aggregateIds,
						status
					) as unknown as NoteListItem[],
					this.noteListAllTotalCount,
					(payload) => this.setAllNoteList(payload)
				);
				setPagedList<NoteListItem>(
					setTheStateForTheAggregateIds(
						this.noteListMine as never[],
						aggregateIds,
						status
					) as unknown as NoteListItem[],
					this.noteListMineTotalCount,
					(payload) => this.setMineNoteList(payload)
				);
			},
			setToday(payload: string): void {
				this.today = payload || '';
			},
			silentSetAllNoteList(): void {
				this.noteListAllTotalCount = 0;
				this.noteListAll = [];
				this.noteListAllSliced.clear();
				this.pagination.set(ActivityListTypeEnum.ALL, createDefaultPaginationEntry());
			},
			silentSetMineNoteList(): void {
				this.noteListMineTotalCount = 0;
				this.noteListMine = [];
				this.noteListMineSliced.clear();
				this.pagination.set(ActivityListTypeEnum.MINE, createDefaultPaginationEntry());
			},
			silentSetOpenNoteList(): void {
				this.noteListOpenTotalCount = 0;
				this.noteListOpen = [];
				this.noteListOpenSliced.clear();
				this.pagination.set(ActivityListTypeEnum.OPEN, createDefaultPaginationEntry());
			},
		},
		getters: {
			paginationFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListPagination {
				return (activityListType: ActivityListTypeEnum): GenericActivityListPagination =>
					state.pagination.get(activityListType) ?? createDefaultPaginationEntry();
			},
			sortFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListSort {
				return (activityListType: ActivityListTypeEnum): GenericActivityListSort =>
					state.sort.get(activityListType) ?? { ...DEFAULT_LIST_SORT };
			},
		},
		state: createNoteListState,
	});

export const useNoteListStore = defineNoteListStore('noteList');

const noteListInstanceDefinitions = new Map<string, ReturnType<typeof defineNoteListStore>>();

export const createNoteListStore = (instanceId: string = DEFAULT_LIST_INSTANCE_ID) => {
	if (isDefaultInstance(instanceId)) {
		return useNoteListStore();
	}

	const storeId = deriveListStoreId('noteList', instanceId);
	let definition = noteListInstanceDefinitions.get(storeId);

	if (definition === undefined) {
		definition = defineNoteListStore(storeId);
		noteListInstanceDefinitions.set(storeId, definition);
	}

	return definition();
};

export * from './interfaces';
export * from './types';
